/**
 * The shape of a plan question's missing connected tool (YAG-986). Every
 * workspace connection reaches a Team by default, so a source a fact needs is
 * either hidden from the Team (`show`, the same route as the older Add) or
 * not connected in the workspace at all (`connect`, on Connections). A caller
 * with no hidden list (an older server) keeps the older `add` shape.
 *
 * One record carries every sentence the three surfaces print (the card, the
 * ask-the-owner button, the comment), so the wording lives in one place.
 */

export type SourceShape = "show" | "connect" | "add";

export function sourceShapeFor(
  hiddenSources: readonly string[] | undefined,
  source: string,
): SourceShape {
  if (hiddenSources === undefined) return "add";
  return hiddenSources.includes(source) ? "show" : "connect";
}

export interface SourceShapeCopy {
  /** The card's sentence: what the asker could check if the source were available. */
  couldCheck: (asker: string, label: string) => string;
  /** The button that makes it available, for whoever can. */
  verb: (label: string) => string;
  /** The same button while it lands. */
  busy: (label: string) => string;
  /** The stamp once it landed. */
  stamp: string;
  /** The ask, as "Ask Priya to <ask>" or "Ask to <ask>". */
  ask: (label: string) => string;
  /** The comment's request, as "please <request> so Reeve can check ...". */
  request: (label: string, team: string) => string;
}

export const SOURCE_SHAPE_COPY: Record<SourceShape, SourceShapeCopy> = {
  show: {
    couldCheck: (asker, label) => `${asker} could check this if ${label} were shown to this Team.`,
    verb: (label) => `Show ${label} to this Team`,
    busy: (label) => `Showing ${label}…`,
    stamp: "shown",
    ask: (label) => `show ${label} to this Team`,
    request: (label, team) => `please show ${label} to ${team}`,
  },
  connect: {
    couldCheck: (asker, label) =>
      `${asker} could check this if ${label} were connected in this workspace.`,
    verb: (label) => `Connect ${label}`,
    busy: (label) => `Connecting ${label}…`,
    stamp: "connected",
    ask: (label) => `connect ${label}`,
    request: (label) => `please connect ${label} on Connections`,
  },
  add: {
    couldCheck: (asker, label) =>
      `${asker} could check this if ${label} were one of this Team’s connected tools.`,
    verb: (label) => `Add ${label} to this Team`,
    busy: (label) => `Adding ${label}…`,
    stamp: "added",
    ask: (label) => `add ${label} to this Team`,
    request: (label, team) => `please add ${label} to ${team}`,
  },
};
